import { useMutation, useQuery } from '@apollo/client'
import { matchSorter } from 'match-sorter'
import React, { useState } from 'react'

import useToggle from '@/gf/hooks/useToggle'
import { UPDATE_SHIPPING_GROUPS } from '@/gf/mutations/shipping'
import { FETCH_SHIPPING_GROUPS } from '@/gf/queries/shipping'
import useSession from '../../hooks/useSession'

import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Modal from '@/gf/components/Modal'
import Spinner from '@/gf/components/Spinner'
import TextField from '@/gf/components/TextField'

import { FiltersVar, UseSelection } from '../../../types'

const groupType = 'ltl'
const cardStyles = {
  className: 'overflow-y-auto',
}

interface IProps {
  selection: UseSelection
  modalOpen: boolean
  onClose: () => void
  filters: FiltersVar
  selectedCount: number
  onComplete: () => void
}

const UpdateShippingLtlModal: React.FC<IProps> = ({
  selection,
  modalOpen,
  onClose,
  filters,
  selectedCount,
  onComplete,
}) => {
  const { user, store } = useSession()
  const [search, setSearch] = useState('')
  const [groupId, setGroupId] = useState(null)
  const [updateShippingLtl] = useMutation(UPDATE_SHIPPING_GROUPS)
  const [spinnerLive, spinner] = useToggle()

  const { loading, data } = useQuery(FETCH_SHIPPING_GROUPS, {
    variables: {
      storeId: store.id,
      groupType: 'ltl',
    },
    fetchPolicy: 'cache-first',
  })

  if (loading) return null

  const allGroups = (data && data.shippingGroups) || []
  const getGroups = () =>
    search
      ? matchSorter(allGroups, search, {
          keys: ['name'],
        })
      : allGroups

  const selectGroup = (id) => {
    setGroupId(id)
  }

  const bulkShippingLtlUpdate = async () => {
    if (groupId) {
      spinner.on()

      await updateShippingLtl({
        variables: {
          productIds: selection.selectedIds,
          groupId,
          groupType,
          allSelected: selection.all,
          filters,
          userId: user.id,
        },
      })

      onComplete()

      spinner.off()
      onClose()
    }
  }

  const results = getGroups()
  const title = `Set shipping ltl on ${selectedCount} products`
  const selectedTitle = groupId ? 'Group Selected' : 'Select a ltl group'

  return (
    <Modal
      open={modalOpen}
      onClose={onClose}
      title={title}
      footerAction={
        <Button title="Confirm" onClick={bulkShippingLtlUpdate} disabled={spinnerLive} />
      }
    >
      {spinnerLive ? (
        <Spinner />
      ) : (
        <Card title={selectedTitle}>
          <Card.Section>
            <TextField label="Search" onChange={(e) => setSearch(e.target.value)} />
          </Card.Section>
          {results.length > 0 && (
            <Card.Section {...cardStyles}>
              <div className="max-h-48">
                {results.map((group) => (
                  <Card.SubSection key={group.id}>
                    <div className="flex space-x-4">
                      <div className="block">
                        <input
                          type="radio"
                          checked={groupId === group.id}
                          onClick={(event) => {
                            event.stopPropagation()
                          }}
                          onChange={() => selectGroup(group.id)}
                        />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <p className="text-sm text-gray-900 font-bold">{group.name}</p>
                      </div>
                    </div>
                  </Card.SubSection>
                ))}
              </div>
            </Card.Section>
          )}
          {results.length === 0 && (
            <div className="p-6 first:border-t-0 border-t-1">
              <div className="flex items-center justify-center">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                  No Groups Found
                </h3>
              </div>
            </div>
          )}
        </Card>
      )}
    </Modal>
  )
}

export default UpdateShippingLtlModal
